/**
 * What the data says, once there is enough of it to say something.
 *
 * Built after maintenance rather than alongside it, on purpose: reporting on
 * data you do not yet collect produces empty charts, and an empty chart loses
 * more trust than a missing screen. Every panel here degrades to a sentence
 * explaining what would fill it.
 *
 * No charting library. These are counts and proportions, and a bar you can read
 * exactly is worth more than one you have to hover to interpret.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, BarChart3, Clock, RefreshCw, Wrench } from 'lucide-react';
import type { ReportSummary } from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { Button } from '../../components/ui/button.js';
import { Select } from '../../components/ui/field.js';
import { Card, CardBody, CardHeader, CardTitle, Code, EmptyState } from '../../components/ui/primitives.js';
import { isNetworkError, reportSummary } from '../../api/client.js';

const STATUS_LABEL: Record<string, string> = {
  IN_STORE: 'In store',
  ISSUED: 'Issued',
  IN_TRANSIT: 'In transit',
  AT_VENDOR: 'At vendor',
  UNDER_MAINTENANCE: 'Maintenance',
  SCRAPPED: 'Scrapped',
};

const TYPE_LABEL: Record<string, string> = {
  PREVENTIVE: 'Preventive',
  BREAKDOWN: 'Breakdown',
  MODIFICATION: 'Modification',
  REFURBISHMENT: 'Refurbishment',
  INSPECTION: 'Inspection',
};

export function ReportsPage() {
  const [days, setDays] = useState('90');
  const [data, setData] = useState<ReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (window: string) => {
    setLoading(true);
    setError(null);
    try {
      setData(await reportSummary(Number(window)));
    } catch (e) {
      setError(
        isNetworkError(e) ? 'No connection. Reports are computed on the server.' : 'Could not load.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [load, days]);

  return (
    <Page
      title="Reports"
      description={
        data ? `Last ${data.windowDays} days` : 'Utilisation, maintenance load and movement.'
      }
      actions={
        <>
          <Select
            aria-label="Reporting window"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            options={[
              { value: '30', label: 'Last 30 days' },
              { value: '90', label: 'Last 90 days' },
              { value: '180', label: 'Last 6 months' },
              { value: '365', label: 'Last year' },
            ]}
            className="w-40"
          />
          <Button variant="secondary" onClick={() => void load(days)} loading={loading}>
            <RefreshCw className="size-4" /> Refresh
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {!data ? (
        <EmptyState
          icon={<BarChart3 />}
          title={loading ? 'Working…' : 'Nothing to report yet'}
          description="Once moulds are scanned and maintenance is recorded, this fills in."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {/* --- headline numbers --- */}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Moulds tracked" value={data.assets.total} />
            <Stat
              label="Never scanned"
              value={data.assets.neverScanned}
              tone={data.assets.neverScanned ? 'warning' : undefined}
            />
            <Stat
              label="Not seen in 30 days"
              value={data.assets.staleOver30Days}
              tone={data.assets.staleOver30Days ? 'warning' : undefined}
            />
            <Stat
              label="Services due"
              value={data.maintenance.dueSchedules}
              tone={data.maintenance.dueSchedules ? 'danger' : undefined}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* --- where things are --- */}
            <Card>
              <CardHeader>
                <CardTitle>Where they are</CardTitle>
              </CardHeader>
              <CardBody>
                <Bars
                  rows={data.assets.byLocation.map((l) => ({ label: l.name, value: l.count }))}
                  total={data.assets.total}
                  empty="Nothing scanned yet."
                />
              </CardBody>
            </Card>

            {/* --- what state they are in --- */}
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardBody>
                <Bars
                  rows={Object.entries(data.assets.byStatus).map(([k, v]) => ({
                    label: STATUS_LABEL[k] ?? k,
                    value: v,
                  }))}
                  total={data.assets.total}
                  empty="No moulds."
                />
              </CardBody>
            </Card>
          </div>

          {/* --- maintenance --- */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="size-4 text-muted-foreground" aria-hidden /> Maintenance
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                {data.maintenance.raised} job{data.maintenance.raised === 1 ? '' : 's'} in this window
              </span>
            </CardHeader>
            <CardBody>
              {data.maintenance.raised === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No maintenance recorded in this window. Raise jobs as work happens and this fills
                  in - downtime, cost, and which tools take the most attention.
                </p>
              ) : (
                <>
                  <div className="mb-4 grid gap-2 sm:grid-cols-4">
                    <Mini label="Open now" value={String(data.maintenance.open)} />
                    <Mini
                      label="Downtime"
                      value={`${data.maintenance.totalDowntimeHours} h`}
                      hint="Entered per job"
                    />
                    <Mini
                      label="Cost"
                      value={data.maintenance.totalCost ? data.maintenance.totalCost.toLocaleString() : '-'}
                      hint="Labour + parts"
                    />
                    <Mini
                      label="Mean days to close"
                      value={
                        data.maintenance.meanDaysToClose == null
                          ? '-'
                          : String(data.maintenance.meanDaysToClose)
                      }
                    />
                  </div>

                  <Bars
                    rows={Object.entries(data.maintenance.byType).map(([k, v]) => ({
                      label: TYPE_LABEL[k] ?? k,
                      value: v,
                      tone: k === 'BREAKDOWN' ? 'danger' : undefined,
                    }))}
                    total={data.maintenance.raised}
                    empty=""
                  />

                  {data.maintenance.worstOffenders.length ? (
                    <>
                      <p className="mb-2 mt-5 text-sm font-medium">Most attention needed</p>
                      {/* Ranked by breakdowns before total jobs: an unplanned
                          stop is what a tool room is trying to eliminate, and a
                          tool with six planned services is doing fine. */}
                      <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="border-b border-border bg-surface-sunken/60 text-left">
                              <th className="px-3 py-1.5 text-xs font-semibold text-muted-foreground">Mould</th>
                              <th className="px-3 py-1.5 text-xs font-semibold text-muted-foreground">Jobs</th>
                              <th className="px-3 py-1.5 text-xs font-semibold text-muted-foreground">Breakdowns</th>
                              <th className="px-3 py-1.5 text-xs font-semibold text-muted-foreground">Downtime</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {data.maintenance.worstOffenders.map((m) => (
                              <tr key={m.assetCode}>
                                <td className="px-3 py-1.5">
                                  <Code>{m.assetCode}</Code>{' '}
                                  <span className="text-muted-foreground">{m.name}</span>
                                </td>
                                <td className="px-3 py-1.5 tabular">{m.jobs}</td>
                                <td className={`px-3 py-1.5 tabular ${m.breakdowns ? 'text-danger' : ''}`}>
                                  {m.breakdowns}
                                </td>
                                <td className="px-3 py-1.5 tabular">{m.downtime || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </CardBody>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* --- life used --- */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="size-4 text-muted-foreground" aria-hidden /> Nearing end
                  of life
                </CardTitle>
              </CardHeader>
              <CardBody>
                {data.life.tracked === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No tool has both a shot count and a rated life yet. Set a rated life on the mould
                    and record readings, and this lists what is approaching replacement.
                  </p>
                ) : data.life.nearingEnd.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nothing above 75% of its rated life. {data.life.tracked} tool
                    {data.life.tracked === 1 ? '' : 's'} tracked.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {data.life.nearingEnd.map((m) => (
                      <li key={m.assetCode}>
                        <div className="flex items-baseline justify-between text-sm">
                          <span>
                            <Code>{m.assetCode}</Code>{' '}
                            <span className="text-muted-foreground">{m.name}</span>
                          </span>
                          <span className={m.pct >= 90 ? 'font-semibold text-danger' : 'font-medium'}>
                            {m.pct}%
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                          <div
                            className={m.pct >= 90 ? 'h-full bg-danger' : 'h-full bg-warning'}
                            style={{ width: `${Math.min(100, m.pct)}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            {/* --- movement --- */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="size-4 text-muted-foreground" aria-hidden /> Scans per week
                </CardTitle>
                <span className="text-xs text-muted-foreground">{data.movement.scans} total</span>
              </CardHeader>
              <CardBody>
                {data.movement.byWeek.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No scans in this window. Movement appears here as the floor records it.
                  </p>
                ) : (
                  <div className="flex h-32 items-end gap-1">
                    {data.movement.byWeek.map((w) => {
                      const max = Math.max(...data.movement.byWeek.map((x) => x.count));
                      return (
                        <div
                          key={w.week}
                          className="flex-1 rounded-t bg-primary"
                          style={{ height: `${Math.max(4, (w.count / max) * 100)}%` }}
                          title={`Week of ${w.week}: ${w.count} scans`}
                        />
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </Page>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'warning' | 'danger' }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-3">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        className={
          tone === 'danger'
            ? 'text-2xl font-semibold text-danger'
            : tone === 'warning'
              ? 'text-2xl font-semibold text-warning-foreground'
              : 'text-2xl font-semibold'
        }
      >
        {value}
      </div>
    </div>
  );
}

function Mini({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg bg-surface-sunken/50 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular">{value}</div>
      {hint ? <div className="text-[11px] text-subtle-foreground">{hint}</div> : null}
    </div>
  );
}

/** Labelled proportional bars. Exact numbers, because these are counts. */
function Bars({
  rows,
  total,
  empty,
}: {
  rows: Array<{ label: string; value: number; tone?: 'danger' }>;
  total: number;
  empty: string;
}) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  const sorted = [...rows].sort((a, b) => b.value - a.value);
  return (
    <ul className="flex flex-col gap-2">
      {sorted.map((r) => (
        <li key={r.label}>
          <div className="flex items-baseline justify-between text-sm">
            <span>{r.label}</span>
            <span className="tabular text-muted-foreground">
              {r.value}
              {total ? ` · ${Math.round((r.value / total) * 100)}%` : ''}
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
            <div
              className={r.tone === 'danger' ? 'h-full bg-danger' : 'h-full bg-primary'}
              style={{ width: `${total ? Math.max(2, (r.value / total) * 100) : 0}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
